import { Component, Input, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import {
    LOT_TYPE_ENUM,
    LOT_JOB_STATUS_ENUM,
    MARKET_STATUS_ENUM,
    ILotDto
} from '@classictechsolutions/hubapi-transpiled-enums';
import { ILotMappedItem, LotsLogicService } from '@app/logic/lots';
import { BehaviorSubject, take } from 'rxjs';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { NavigationService } from '@app/core/services/navigation/navigation.service';
import { orderBy } from 'lodash';
import { CbDialogService } from '@app/shared/components/dialog/cb-dialog.service';
import { MatSlideToggle, MatSlideToggleChange } from '@angular/material/slide-toggle';

const ORDER_PROP = 'unitConstructionOrder';

@Component({
    selector: 'cb-lot-units-list',
    templateUrl: './lot-units-list.component.html',
    styleUrls: ['./lot-units-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class LotUnitsListComponent {
    public readonly LOT_TYPE_ENUM = LOT_TYPE_ENUM;
    public readonly LOT_JOB_STATUS_ENUM = LOT_JOB_STATUS_ENUM;
    public readonly MARKET_STATUS_ENUM = MARKET_STATUS_ENUM;
    public sortOrderChanged = false;
    public lotsLoaded = false;
    public reorderEnabled = false;
    public parentLotHasBuildProgrammeGenerated = false;

    public editableOrder: { [lotId: number]: number } = {};
    private originalOrder: { [lotId: number]: number } = {};

    public readonly displayedColumns: string[] = [
        'lotNumber',
        'address',
        'lotType',
        'buildLink',
        'floorArea',
        'landPrice',
        'sellPrice',
        'contractPrice',
        'jobNumber',
        'jobStatus',
        'bed',
        'bath',
        'living',
        'garage',
        'marketStatus',
        'actions'
    ];

    public lots: ILotDto[] = [];

    public lotMappedItem$$: BehaviorSubject<ILotMappedItem> = new BehaviorSubject(null);

    @Input() public set lotMappedItem(mappedItem: ILotMappedItem) {
        if (mappedItem) {
            this.lotMappedItem$$.next(mappedItem);

            this.parentLotHasBuildProgrammeGenerated = mappedItem.hasBuildProgrammeGenerated;
            this.lotsLogic
                .getUnits(mappedItem.id)
                .pipe(take(1))
                .subscribe(units => {
                    this.lots = units;
                    this.loadLots();
                });
        }
    }

    public drop(event: CdkDragDrop<ILotDto[]>): void {
        if (!this.reorderingAllowed || !this.reorderEnabled) {
            return;
        }

        moveItemInArray(this.lots, event.previousIndex, event.currentIndex);

        this.lots = [...this.lots];

        this.lots.forEach((lot, index) => {
            this.editableOrder[lot.id] = index + 1;
        });

        this.sortOrderChanged = true;
    }

    public get lotMappedItem(): ILotMappedItem {
        return this.lotMappedItem$$?.value;
    }

    public get reorderingAllowed(): boolean {
        return !this.lots?.some(x => x.hasBuildProgrammeGenerated);
    }

    constructor(
        private readonly lotsLogic: LotsLogicService,
        private readonly navigation: NavigationService,
        private readonly cbDialog: CbDialogService,
        private readonly cdRef: ChangeDetectorRef
    ) {}

    public toggleReorder(event: MatSlideToggleChange): void {
        if (!event.checked && this.sortOrderChanged) {
            this.cbDialog.confirm({
                dialogHeading: 'Discard Changes',
                message: 'Are you sure you want to discard your changes?',
                confirmed: () => {
                    this.setReorderToggle(false, event.source);
                    this.updateLotOrder();
                },
                declined: () => {
                    this.setReorderToggle(true, event.source);
                }
            });
        } else {
            this.setReorderToggle(event.checked, event.source);
            this.editableOrder = {};
        }
    }

    public saveOrder(): void {
        this.lotsLogic
            .reorderUnits(this.lotMappedItem.id, this.editableOrder)
            .pipe(take(1))
            .subscribe(result => {
                this.originalOrder = result;
                this.setReorderToggle(false);
                this.updateLotOrder();
            });
    }

    public viewLot(event: MouseEvent, lotId: number): void {
        this.navigation.redirectTo(`lots/${lotId}/summary`);
    }

    private setReorderToggle(value: boolean, source?: MatSlideToggle): void {
        this.reorderEnabled = value;
        if (source) {
            source.checked = value;
        }
        this.cdRef.detectChanges();
    }

    private loadLots(): void {
        this.lots = orderBy(this.lots, ORDER_PROP);
        this.lots.forEach(lot => {
            this.originalOrder[lot.id] = lot.unitConstructionOrder;
        });
        this.lotsLoaded = true;
        this.cdRef.detectChanges();
    }

    private updateLotOrder(): void {
        this.lots.forEach(lot => {
            lot.unitConstructionOrder = this.originalOrder[lot.id];
        });
        this.editableOrder = {};
        const ordered = orderBy(this.lots, ORDER_PROP);
        this.sortOrderChanged = false;
        // set the lots to empty and detect changes before reordering, otherwise the reordering is not rendered in the ui
        this.lots = [];
        this.cdRef.detectChanges();
        this.lots = ordered;
        this.cdRef.detectChanges();
    }
}
